"use client";

import React, { useEffect, useMemo, useState } from 'react';

export interface PasswordLoginFormValues {
  login: string;
  password: string;
  remember: boolean;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  marketingAccepted: boolean;
}

export interface PasswordLoginFormProps {
  onSubmit: (values: PasswordLoginFormValues) => Promise<void> | void;
  initialValues?: Partial<PasswordLoginFormValues>;
  submitting?: boolean;
  errorMessage?: string | null;
  title?: string;
  description?: string;
  rememberLabel?: string;
  submitLabel?: string;
  loginLabel?: string;
  passwordLabel?: string;
  onValuesChange?: (values: PasswordLoginFormValues) => void;
  agreements?: {
    terms?: React.ReactNode;
    privacy?: React.ReactNode;
    marketing?: React.ReactNode;
    selectAllLabel?: React.ReactNode;
  };
  agreementsErrorMessage?: string;
}

const DEFAULT_VALUES: PasswordLoginFormValues = {
  login: '',
  password: '',
  remember: true,
  termsAccepted: false,
  privacyAccepted: false,
  marketingAccepted: false
};

export const PasswordLoginForm: React.FC<PasswordLoginFormProps> = ({
  onSubmit,
  initialValues,
  submitting = false,
  errorMessage,
  title = 'Вход в личный кабинет',
  description = 'Введите логин (телефон или email) и пароль, указанные при регистрации.',
  submitLabel = 'Войти',
  loginLabel = 'Логин или email',
  passwordLabel = 'Пароль',
  onValuesChange,
  agreements,
  agreementsErrorMessage = 'Для продолжения отметьте обязательные согласия'
}) => {
  const mergedInitial = useMemo(
    () => ({
      ...DEFAULT_VALUES,
      ...(initialValues ?? {})
    }),
    [initialValues]
  );

  const [values, setValues] = useState<PasswordLoginFormValues>(mergedInitial);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setValues(mergedInitial);
  }, [mergedInitial]);

  useEffect(() => {
    if (errorMessage) {
      setLocalError(errorMessage);
    } else {
      setLocalError(null);
    }
  }, [errorMessage]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!values.login || !values.password) {
      setLocalError('Пожалуйста, заполните логин и пароль');
      return;
    }

     if (!values.termsAccepted || !values.privacyAccepted) {
      setLocalError(agreementsErrorMessage);
      return;
    }

    await onSubmit(values);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    const nextValues: PasswordLoginFormValues = {
      ...values,
      [name]: type === 'checkbox' ? checked : value
    };

    setValues(nextValues);
    setLocalError(null);
    onValuesChange?.(nextValues);
  };

  const allChecked =
    values.termsAccepted && values.privacyAccepted && values.marketingAccepted;

  const toggleAllAgreements = () => {
    const toggled = !allChecked;
    const nextValues: PasswordLoginFormValues = {
      ...values,
      termsAccepted: toggled,
      privacyAccepted: toggled,
      marketingAccepted: toggled
    };

    setValues(nextValues);
    setLocalError(null);
    if (onValuesChange) {
      onValuesChange(nextValues);
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
        <p className="text-sm text-gray-500">{description}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label htmlFor="login" className="block text-sm font-medium text-gray-700">
            {loginLabel}
          </label>
          <input
            id="login"
            name="login"
            value={values.login}
            onChange={handleChange}
            autoComplete="username"
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:bg-gray-100"
            placeholder="mail@example.com"
            disabled={submitting}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            {passwordLabel}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            value={values.password}
            onChange={handleChange}
            autoComplete="current-password"
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:bg-gray-100"
            placeholder="••••••••"
            disabled={submitting}
          />
        </div>

        <div className="space-y-3 text-sm text-gray-700 py-4">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={values.termsAccepted}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 transition focus:ring-blue-500"
              disabled={submitting}
            />
            <span className="leading-5">
              {agreements?.terms ?? (
                <>
                  Я согласен с условиями{' '}
                  <a href="/legal/user-agreement" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                    Пользовательского соглашения
                  </a>{' '}
                  и{' '}
                  <a href="/legal/oferta" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                    Публичной оферты
                  </a>
                </>
              )}
              <span className="ml-1 text-red-500">*</span>
            </span>
          </label>

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              name="privacyAccepted"
              checked={values.privacyAccepted}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 transition focus:ring-blue-500"
              disabled={submitting}
            />
            <span className="leading-5">
              {agreements?.privacy ?? (
                <>
                  Я даю согласие на обработку Персональных данных в соответствии с{' '}
                  <a href="/legal/politika-konfidentsialnosti" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                    Политикой обработки и защиты Персональных данных
                  </a>
                </>
              )}
              <span className="ml-1 text-red-500">*</span>
            </span>
          </label>

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              name="marketingAccepted"
              checked={values.marketingAccepted}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 transition focus:ring-blue-500"
              disabled={submitting}
            />
            <span className="leading-5">
              {agreements?.marketing ?? 'Я согласен на получение рекламных и информационных сообщений'}
            </span>
          </label>

          <button
            type="button"
            onClick={toggleAllAgreements}
            className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-blue-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={submitting}
          >
            {agreements?.selectAllLabel ??
              (allChecked ? 'Снять все согласия' : 'Отметить все согласия')}
          </button>
        </div>

        {(localError || errorMessage) && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {localError || errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        >
          {submitting ? 'Входим...' : submitLabel}
        </button>
      </form>
    </div>
  );
};

PasswordLoginForm.displayName = 'PasswordLoginForm';
